import { Check, ChevronDown, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { cn } from "../../lib/utils";
import { Input } from "./input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";

export type SearchableMenuOption = {
  value: string;
  label: string;
  searchText?: string;
  disabled?: boolean;
};

type SearchableMenuSelectProps = {
  value: string;
  options: SearchableMenuOption[];
  placeholder: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  noResultsText?: string;
  searchPlaceholder?: string;
  align?: "start" | "center" | "end";
};

export function SearchableMenuSelect({
  value,
  options,
  placeholder,
  onChange,
  className,
  disabled = false,
  noResultsText = "No fields found",
  searchPlaceholder = "Search fields...",
  align = "start",
}: SearchableMenuSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return options;
    return options.filter((option) => {
      const haystack = `${option.label} ${option.searchText || ""}`.toLowerCase();
      return haystack.includes(search);
    });
  }, [options, query]);

  useEffect(() => {
    if (!isOpen) setQuery("");
  }, [isOpen]);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex items-center justify-between gap-2 text-left",
            !selectedOption && "text-muted-foreground",
            className,
          )}
        >
          <span className="truncate">{selectedOption?.label || placeholder}</span>
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-[var(--radix-dropdown-menu-trigger-width)] p-0">
        <div className="border-b border-border-chip p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              onKeyDown={(event) => event.stopPropagation()}
              placeholder={searchPlaceholder}
              className="h-8 pl-8"
            />
          </div>
        </div>
        <div className="max-h-[260px] overflow-y-auto py-1">
          {filteredOptions.length ? (
            filteredOptions.map((option) => (
              <DropdownMenuItem
                key={option.value || "__empty__"}
                disabled={option.disabled}
                onSelect={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={cn(option.value === value && "bg-accent-tint text-primary")}
              >
                <div className="flex w-full items-center gap-2">
                  <span className="inline-flex h-4 w-4 items-center justify-center">
                    {option.value === value ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
                  </span>
                  <span className="truncate">{option.label}</span>
                </div>
              </DropdownMenuItem>
            ))
          ) : (
            <div className="px-3 py-2 text-xs text-muted-foreground">{noResultsText}</div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

